import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MapPin, ArrowUpDown, Calendar, Clock, Users, Briefcase, Plane,
  MessageSquare, Tag, ArrowRight, ArrowLeft, Check, Baby, Wifi,
  Languages, RotateCcw, X, Plus, Minus, Mail, Phone, User, Shield, Loader2
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TrustBadges } from '../components/trust/TrustBadges';
import { CancellationBanner } from '../components/trust/CancellationBanner';
import { ExitIntentPopup } from '../components/trust/ExitIntentPopup';
import { useABVariant } from '../components/trust/useABVariant';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom markers
const startIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#FF6B00" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#FF6B00" font-size="10" font-weight="bold">A</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

const endIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36"><path fill="#000000" d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24c0-6.6-5.4-12-12-12z"/><circle fill="white" cx="12" cy="12" r="6"/><text x="12" y="16" text-anchor="middle" fill="#000000" font-size="10" font-weight="bold">B</text></svg>`),
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

// Component to fit map bounds
function FitBounds({ from, to }) {
  const map = useMap();
  useEffect(() => {
    if (from && to) {
      const bounds = L.latLngBounds([
        [from.lat, from.lng],
        [to.lat, to.lng]
      ]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, from, to]);
  return null;
}

// Vehicle types with realistic images - used as fallback
const FALLBACK_VEHICLE_TYPES = [
  { id: 'economy', name: 'Economy', passengers: 4, luggage: 3, basePrice: 31 },
  { id: 'comfort', name: 'Comfort', passengers: 4, luggage: 3, basePrice: 38 },
  { id: 'business', name: 'Business', passengers: 3, luggage: 3, basePrice: 48 },
  { id: 'premium', name: 'Premium', passengers: 3, luggage: 3, basePrice: 50 },
  { id: 'vip', name: 'VIP', passengers: 3, luggage: 4, basePrice: 99 },
  { id: 'suv', name: 'SUV', passengers: 6, luggage: 5, basePrice: 39 },
  { id: 'van', name: 'Van', passengers: 8, luggage: 8, basePrice: 37 },
  { id: 'minibus', name: 'Minibus', passengers: 16, luggage: 16, basePrice: 100 },
  { id: 'bus', name: 'Bus', passengers: 50, luggage: 50, basePrice: 207 },
];

// Vehicle SVG images
const VehicleImages = {
  economy: (
    <svg viewBox="0 0 120 60" className="w-full h-auto">
      <ellipse cx="60" cy="55" rx="45" ry="4" fill="#00000015"/>
      <path d="M15 40 L20 25 L40 20 L80 20 L100 25 L105 40 L105 48 L15 48 Z" fill="#333" stroke="#222" strokeWidth="1"/>
      <path d="M25 20 L35 12 L75 12 L90 20" fill="#222" stroke="#222" strokeWidth="1"/>
      <rect x="30" y="14" width="20" height="8" rx="1" fill="#87CEEB" opacity="0.6"/>
      <rect x="55" y="14" width="25" height="8" rx="1" fill="#87CEEB" opacity="0.6"/>
      <circle cx="30" cy="48" r="8" fill="#222"/><circle cx="30" cy="48" r="5" fill="#666"/>
      <circle cx="90" cy="48" r="8" fill="#222"/><circle cx="90" cy="48" r="5" fill="#666"/>
      <rect x="100" y="35" width="6" height="4" rx="1" fill="#ff4444"/>
      <rect x="14" y="35" width="6" height="3" rx="1" fill="#ffff66"/>
    </svg>
  ),
  comfort: (
    <svg viewBox="0 0 120 60" className="w-full h-auto">
      <ellipse cx="60" cy="55" rx="45" ry="4" fill="#00000015"/>
      <path d="M12 40 L18 22 L38 16 L82 16 L102 22 L108 40 L108 48 L12 48 Z" fill="#1a1a1a" stroke="#111" strokeWidth="1"/>
      <path d="M25 16 L32 8 L78 8 L92 16" fill="#111" stroke="#111" strokeWidth="1"/>
      <rect x="28" y="10" width="22" height="8" rx="1" fill="#87CEEB" opacity="0.5"/>
      <rect x="54" y="10" width="28" height="8" rx="1" fill="#87CEEB" opacity="0.5"/>
      <circle cx="28" cy="48" r="9" fill="#111"/><circle cx="28" cy="48" r="6" fill="#444"/>
      <circle cx="92" cy="48" r="9" fill="#111"/><circle cx="92" cy="48" r="6" fill="#444"/>
      <rect x="102" y="34" width="7" height="5" rx="1" fill="#ff3333"/>
      <rect x="11" y="34" width="7" height="4" rx="1" fill="#ffff55"/>
    </svg>
  ),
  business: (
    <svg viewBox="0 0 120 60" className="w-full h-auto">
      <ellipse cx="60" cy="55" rx="48" ry="4" fill="#00000015"/>
      <path d="M8 38 L15 20 L40 14 L85 14 L105 20 L112 38 L112 48 L8 48 Z" fill="#0a0a0a" stroke="#000" strokeWidth="1"/>
      <path d="M22 14 L30 5 L80 5 L95 14" fill="#000" stroke="#000" strokeWidth="1"/>
      <rect x="26" y="7" width="24" height="9" rx="2" fill="#6BB8E8" opacity="0.4"/>
      <rect x="54" y="7" width="30" height="9" rx="2" fill="#6BB8E8" opacity="0.4"/>
      <circle cx="26" cy="48" r="9" fill="#000"/><circle cx="26" cy="48" r="6" fill="#333"/><circle cx="26" cy="48" r="3" fill="#555"/>
      <circle cx="94" cy="48" r="9" fill="#000"/><circle cx="94" cy="48" r="6" fill="#333"/><circle cx="94" cy="48" r="3" fill="#555"/>
      <rect x="105" y="32" width="8" height="6" rx="2" fill="#cc2222"/>
      <rect x="7" y="32" width="8" height="5" rx="2" fill="#eeee44"/>
      <path d="M45 14 L45 7" stroke="#222" strokeWidth="3"/>
    </svg>
  ),
  premium: (
    <svg viewBox="0 0 130 60" className="w-full h-auto">
      <ellipse cx="65" cy="55" rx="52" ry="4" fill="#00000015"/>
      <path d="M5 36 L14 18 L45 12 L95 12 L115 18 L125 36 L125 48 L5 48 Z" fill="#0a0a0a" stroke="#000" strokeWidth="1"/>
      <path d="M20 12 L30 3 L90 3 L108 12" fill="#000" stroke="#000" strokeWidth="1"/>
      <rect x="26" y="5" width="28" height="9" rx="2" fill="#5BAAD8" opacity="0.35"/>
      <rect x="58" y="5" width="38" height="9" rx="2" fill="#5BAAD8" opacity="0.35"/>
      <circle cx="24" cy="48" r="10" fill="#000"/><circle cx="24" cy="48" r="7" fill="#2a2a2a"/><circle cx="24" cy="48" r="3" fill="#444"/>
      <circle cx="106" cy="48" r="10" fill="#000"/><circle cx="106" cy="48" r="7" fill="#2a2a2a"/><circle cx="106" cy="48" r="3" fill="#444"/>
      <rect x="117" y="30" width="9" height="6" rx="2" fill="#bb1111"/>
      <rect x="4" y="30" width="10" height="5" rx="2" fill="#dddd33"/>
      <line x1="65" y1="48" x2="65" y2="36" stroke="#1a1a1a" strokeWidth="1"/>
    </svg>
  ),
  vip: (
    <svg viewBox="0 0 130 60" className="w-full h-auto">
      <ellipse cx="65" cy="55" rx="52" ry="4" fill="#00000015"/>
      <path d="M5 35 L12 16 L42 10 L98 10 L118 16 L125 35 L125 48 L5 48 Z" fill="#080808" stroke="#000" strokeWidth="1"/>
      <path d="M18 10 L28 2 L92 2 L110 10" fill="#000"/>
      <rect x="24" y="4" width="30" height="8" rx="2" fill="#4A9CC8" opacity="0.3"/>
      <rect x="58" y="4" width="40" height="8" rx="2" fill="#4A9CC8" opacity="0.3"/>
      <circle cx="22" cy="48" r="10" fill="#000"/><circle cx="22" cy="48" r="7" fill="#1a1a1a"/><circle cx="22" cy="48" r="4" fill="#333"/>
      <circle cx="108" cy="48" r="10" fill="#000"/><circle cx="108" cy="48" r="7" fill="#1a1a1a"/><circle cx="108" cy="48" r="4" fill="#333"/>
      <rect x="118" y="28" width="8" height="7" rx="2" fill="#aa0000"/>
      <rect x="4" y="28" width="9" height="6" rx="2" fill="#cccc22"/>
      <rect x="58" y="36" width="14" height="2" fill="#c0c0c0" opacity="0.3"/>
    </svg>
  ),
  suv: (
    <svg viewBox="0 0 130 65" className="w-full h-auto">
      <ellipse cx="65" cy="60" rx="50" ry="4" fill="#00000015"/>
      <path d="M10 42 L18 22 L35 12 L95 12 L112 22 L120 42 L120 52 L10 52 Z" fill="#1a1a1a" stroke="#111" strokeWidth="1"/>
      <path d="M28 12 L35 4 L85 4 L100 12" fill="#111"/>
      <rect x="32" y="6" width="22" height="8" rx="1" fill="#87CEEB" opacity="0.4"/>
      <rect x="58" y="6" width="30" height="8" rx="1" fill="#87CEEB" opacity="0.4"/>
      <rect x="18" y="20" width="8" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="104" y="20" width="8" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <circle cx="28" cy="52" r="11" fill="#111"/><circle cx="28" cy="52" r="7" fill="#333"/>
      <circle cx="102" cy="52" r="11" fill="#111"/><circle cx="102" cy="52" r="7" fill="#333"/>
      <rect x="112" y="36" width="9" height="6" rx="2" fill="#dd2222"/>
      <rect x="9" y="36" width="9" height="5" rx="2" fill="#eeee44"/>
    </svg>
  ),
  van: (
    <svg viewBox="0 0 130 65" className="w-full h-auto">
      <ellipse cx="65" cy="60" rx="50" ry="4" fill="#00000015"/>
      <path d="M8 50 L8 18 L30 10 L100 10 L115 20 L122 38 L122 50 Z" fill="#1a1a1a" stroke="#111" strokeWidth="1"/>
      <rect x="12" y="14" width="14" height="22" rx="2" fill="#87CEEB" opacity="0.4"/>
      <rect x="30" y="14" width="18" height="18" rx="1" fill="#87CEEB" opacity="0.35"/>
      <rect x="52" y="14" width="18" height="18" rx="1" fill="#87CEEB" opacity="0.35"/>
      <rect x="74" y="14" width="18" height="18" rx="1" fill="#87CEEB" opacity="0.35"/>
      <circle cx="28" cy="50" r="10" fill="#111"/><circle cx="28" cy="50" r="6" fill="#444"/>
      <circle cx="102" cy="50" r="10" fill="#111"/><circle cx="102" cy="50" r="6" fill="#444"/>
      <rect x="114" y="40" width="9" height="6" rx="2" fill="#dd2222"/>
      <rect x="7" y="40" width="8" height="5" rx="2" fill="#eeee44"/>
    </svg>
  ),
  minibus: (
    <svg viewBox="0 0 140 65" className="w-full h-auto">
      <ellipse cx="70" cy="60" rx="55" ry="4" fill="#00000015"/>
      <path d="M5 50 L5 16 L25 8 L115 8 L130 18 L135 38 L135 50 Z" fill="#1a1a1a" stroke="#111" strokeWidth="1"/>
      <rect x="10" y="12" width="16" height="24" rx="2" fill="#87CEEB" opacity="0.4"/>
      <rect x="30" y="12" width="14" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="48" y="12" width="14" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="66" y="12" width="14" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="84" y="12" width="14" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="102" y="12" width="14" height="18" rx="1" fill="#87CEEB" opacity="0.3"/>
      <circle cx="25" cy="50" r="10" fill="#111"/><circle cx="25" cy="50" r="6" fill="#444"/>
      <circle cx="115" cy="50" r="10" fill="#111"/><circle cx="115" cy="50" r="6" fill="#444"/>
      <rect x="126" y="38" width="10" height="7" rx="2" fill="#dd2222"/>
      <rect x="4" y="38" width="9" height="6" rx="2" fill="#eeee44"/>
    </svg>
  ),
  bus: (
    <svg viewBox="0 0 160 70" className="w-full h-auto">
      <ellipse cx="80" cy="65" rx="65" ry="4" fill="#00000015"/>
      <path d="M5 55 L5 14 L20 6 L140 6 L155 16 L155 55 Z" fill="#222" stroke="#111" strokeWidth="1"/>
      <rect x="10" y="10" width="18" height="28" rx="2" fill="#87CEEB" opacity="0.4"/>
      <rect x="32" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="50" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="68" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="86" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="104" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <rect x="122" y="10" width="14" height="22" rx="1" fill="#87CEEB" opacity="0.3"/>
      <circle cx="30" cy="55" r="12" fill="#111"/><circle cx="30" cy="55" r="8" fill="#333"/>
      <circle cx="130" cy="55" r="12" fill="#111"/><circle cx="130" cy="55" r="8" fill="#333"/>
      <rect x="145" y="42" width="11" height="8" rx="2" fill="#cc1111"/>
      <rect x="4" y="42" width="10" height="7" rx="2" fill="#dddd22"/>
      <line x1="75" y1="6" x2="75" y2="55" stroke="#333" strokeWidth="1"/>
    </svg>
  ),
};

const QUICK_TAGS = [
  { id: 'wifi', label: 'Ich brauche Wi-Fi', icon: Wifi },
  { id: 'english', label: 'Englischsprachiger Fahrer', icon: Languages },
  { id: 'rental', label: 'Interesse an Mietwagen', icon: null },
];

export default function BookingDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, login, register, verifyEmail, resendCode, loadFromStorage } = useAuthStore();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const { isB: trustB } = useABVariant('trust_ui_v1');

  // Dynamic vehicle types from CMS
  const [dynamicVehicleTypes, setDynamicVehicleTypes] = useState(null);
  const VEHICLE_TYPES = useMemo(() => {
    if (!dynamicVehicleTypes) return FALLBACK_VEHICLE_TYPES;
    return dynamicVehicleTypes.map(cat => ({
      id: cat.vehicle_id,
      name: cat.name,
      passengers: cat.passengers,
      luggage: cat.luggage,
      basePrice: cat.base_price,
      image_path: cat.image_path,
      category_id: cat.id,
    }));
  }, [dynamicVehicleTypes]);

  useEffect(() => {
    api.get('/vehicle-categories').then(res => {
      if (res.data && res.data.length > 0) setDynamicVehicleTypes(res.data);
    }).catch(() => {});
  }, []);

  // Auth modal state
  const [authStep, setAuthStep] = useState('choose'); // 'choose' | 'login' | 'register' | 'verify'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  useEffect(() => { document.title = 'Flughafentransfer online buchen | Angebote — 366Transfer'; }, []);

  // Get data from navigation state or localStorage
  const [routeData, setRouteData] = useState(() => {
    // Try to get from navigation state first
    if (location.state?.fromLocation && location.state?.toLocation) {
      // Save to localStorage for persistence
      localStorage.setItem('bookingRouteData', JSON.stringify(location.state));
      return location.state;
    }
    // Try to get from localStorage as fallback (defensive: ignore corrupted string-only data)
    const stored = localStorage.getItem('bookingRouteData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Guard: fromLocation/toLocation must be objects with .lat/.lng, not strings
        if (parsed?.fromLocation?.lat != null && parsed?.toLocation?.lat != null) {
          return parsed;
        }
        // Corrupted shape → drop it
        localStorage.removeItem('bookingRouteData');
      } catch {
        localStorage.removeItem('bookingRouteData');
      }
    }
    return {};
  });

  const { fromLocation, toLocation, routeInfo } = routeData;

  // Fetch route from OSRM
  useEffect(() => {
    async function fetchRoute() {
      if (!fromLocation || !toLocation) return;
      
      try {
        const response = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${fromLocation.lng},${fromLocation.lat};${toLocation.lng},${toLocation.lat}?overview=full&geometries=geojson`
        );
        const data = await response.json();
        
        if (data.routes && data.routes[0]) {
          const coords = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
          setRouteCoordinates(coords);
        }
      } catch (error) {
        console.error('Error fetching route:', error);
        // Fallback: straight line
        setRouteCoordinates([
          [fromLocation.lat, fromLocation.lng],
          [toLocation.lat, toLocation.lng]
        ]);
      }
    }
    
    fetchRoute();
  }, [fromLocation, toLocation]);

  // Form state
  const [selectedVehicle, setSelectedVehicle] = useState('economy');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [nameOnSign, setNameOnSign] = useState('');
  const [addReturn, setAddReturn] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [luggage, setLuggage] = useState(2);
  const [childSeats, setChildSeats] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [showPromo, setShowPromo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showChildrenModal, setShowChildrenModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    loadFromStorage();
    // Set default date/time
    const now = new Date();
    now.setHours(now.getHours() + 2);
    setPickupDate(now.toISOString().split('T')[0]);
    setPickupTime(now.toTimeString().slice(0, 5));
  }, [loadFromStorage]);

  // Auto-submit after successful login
  useEffect(() => {
    if (pendingSubmit && user && token) {
      setPendingSubmit(false);
      setShowAuthModal(false);
      handleSubmit();
    }
  }, [user, token, pendingSubmit]);

  const handleGoogleLogin = () => {
    // Store that we need to auto-submit after Google login
    localStorage.setItem('pendingBookingSubmit', 'true');
    const redirectUrl = window.location.origin + '/booking';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Check for pending submit after Google login redirect
  useEffect(() => {
    if (user && token && localStorage.getItem('pendingBookingSubmit') === 'true') {
      localStorage.removeItem('pendingBookingSubmit');
      // Small delay to let the page settle
      setTimeout(() => handleSubmit(), 500);
    }
  }, [user, token]);

  const handleAuthLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = await login(authEmail, authPassword);
      if (result?.requires_2fa) {
        setAuthError('Admin-Login ist hier nicht möglich');
      } else {
        setPendingSubmit(true);
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Fehler beim Anmelden');
    } finally { setAuthLoading(false); }
  };

  const handleAuthRegister = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const result = await register(authEmail, authPassword, authName, authPhone);
      if (result?.requires_verification) {
        setAuthStep('verify');
      } else {
        setPendingSubmit(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('bereits registriert') || detail.includes('already registered')) {
        setAuthError('Diese E-Mail ist bereits registriert.');
        setTimeout(() => setAuthStep('login'), 1500);
      } else {
        setAuthError(detail || 'Fehler beim Registrieren. Bitte versuchen Sie es erneut.');
      }
    } finally { setAuthLoading(false); }
  };

  const handleAuthVerify = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      await verifyEmail(authEmail, authCode);
      setPendingSubmit(true);
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Ungültiger Code');
    } finally { setAuthLoading(false); }
  };

  const resetAuthModal = () => {
    setShowAuthModal(false);
    setAuthStep('choose');
    setAuthEmail('');
    setAuthPassword('');
    setAuthName('');
    setAuthPhone('');
    setAuthCode('');
    setAuthError('');
  };

  // Redirect back if no route data (after checking localStorage)
  useEffect(() => {
    if (!fromLocation || !toLocation) {
      // Small delay to allow state to be loaded
      const timer = setTimeout(() => {
        if (!fromLocation || !toLocation) {
          navigate('/');
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fromLocation, toLocation, navigate]);

  const calculatePrice = (vehicleType) => {
    const vehicle = VEHICLE_TYPES.find(v => v.id === vehicleType);
    if (!vehicle || !routeInfo) return vehicle?.basePrice || 0;
    
    const distanceFactor = routeInfo.distance / 100;
    const price = Math.round(vehicle.basePrice + (vehicle.basePrice * distanceFactor * 0.5));
    return addReturn ? price * 2 : price;
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) ? prev.filter(t => t !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async () => {
    if (!user || !token) {
      setShowAuthModal(true);
      return;
    }

    if (!pickupDate || !pickupTime) {
      alert('Bitte wählen Sie Datum und Uhrzeit');
      return;
    }

    setIsLoading(true);
    try {
      // Send pickup_date as naive local time (NO UTC conversion).
      // The customer enters the local pickup time (e.g. Hurghada 01:20),
      // and we want everyone — customer, driver, admin — to see exactly
      // that time regardless of their browser timezone.
      const pickupDateStr = `${pickupDate}T${pickupTime}:00`;
      const requestData = {
        pickup_location: fromLocation,
        dropoff_location: toLocation,
        pickup_date: pickupDateStr,
        flight_number: flightNumber || null,
        name_on_sign: nameOnSign || null,
        adults,
        children,
        child_seats: childSeats,
        luggage: luggage,
        preferred_vehicle_type: selectedVehicle,
        distance_km: routeInfo?.distance || 0,
        duration_minutes: routeInfo?.duration || 0,
        comment: comment || null,
        tags: selectedTags,
        promo_code: promoCode || null,
        has_return: addReturn,
        return_date: addReturn && returnDate ? `${returnDate}T${returnTime || '12:00'}:00` : null,
      };

      await api.post('/transfers', requestData);
      // Clear stored route data after successful booking
      localStorage.removeItem('bookingRouteData');
      alert('Transfer-Anfrage erstellt! Fahrer werden bald Angebote senden.');
      navigate('/my-transfers');
    } catch (err) {
      alert(err.response?.data?.detail || 'Fehler beim Erstellen der Anfrage');
    } finally {
      setIsLoading(false);
    }
  };

  if (!fromLocation || !toLocation) {
    return null;
  }

  // Map center calculation
  const mapCenter = [
    (fromLocation.lat + toLocation.lat) / 2,
    (fromLocation.lng + toLocation.lng) / 2
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="booking-details-page">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <a href="/" className="flex items-baseline gap-0.5">
              <span className="text-2xl font-extrabold text-orange-500">366</span>
              <span className="text-base font-semibold text-slate-800">Transfer</span>
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
          Beste Preise von den besten Fahrern
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-8">
          {['Transfers', 'Überlandfahrten', 'Stündlich', 'Lieferung'].map((item, i) => (
            <span key={item} className="flex items-center gap-1">
              <Check size={14} className="text-emerald-500" /> {item}
            </span>
          ))}
          <span className="flex items-center gap-1 text-orange-500 font-medium">
            <Check size={14} /> Ausschreibungsbasierte Preise
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Side - Form */}
          <div className="space-y-6">
            {/* Route Display */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="space-y-3">
                {/* From */}
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">A</div>
                    <div className="w-0.5 h-8 bg-slate-200 my-1" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-800 font-medium">{fromLocation.address}</p>
                  </div>
                  <button onClick={() => navigate('/')} className="text-slate-300 hover:text-slate-500">
                    <X size={18} />
                  </button>
                </div>

                {/* Swap Button */}
                <div className="flex justify-center -my-2">
                  <button className="w-8 h-8 rounded-full border-2 border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:border-orange-500 hover:text-orange-500 transition-colors">
                    <ArrowUpDown size={14} />
                  </button>
                </div>

                {/* To */}
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">B</div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-800 font-medium">{toLocation.address}</p>
                  </div>
                  <button onClick={() => navigate('/')} className="text-slate-300 hover:text-slate-500">
                    <X size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Vehicle Selection */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Fahrzeug wählen</h3>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {VEHICLE_TYPES.map((vehicle) => {
                  const price = calculatePrice(vehicle.id);
                  const isSelected = selectedVehicle === vehicle.id;
                  return (
                    <button
                      key={vehicle.id}
                      onClick={() => setSelectedVehicle(vehicle.id)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-100 hover:border-slate-200 bg-white'
                      }`}
                      data-testid={`vehicle-${vehicle.id}`}
                    >
                      <div className="h-16 flex items-center justify-center mb-2 px-2">
                        {vehicle.image_path ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-categories/${vehicle.category_id}/image`}
                            alt={vehicle.name}
                            className="h-full w-auto object-contain"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                          />
                        ) : null}
                        <div style={{ display: vehicle.image_path ? 'none' : 'block' }} className="w-full">
                          {VehicleImages[vehicle.id]}
                        </div>
                      </div>
                      <p className={`text-xs font-medium ${isSelected ? 'text-orange-700' : 'text-slate-700'}`}>
                        {vehicle.name}
                      </p>
                      <p className={`text-xs ${isSelected ? 'text-orange-500' : 'text-emerald-600'}`}>
                        ab €{price}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date & Time */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Abholtermin</span>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    data-testid="pickup-date"
                  />
                  <input
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    data-testid="pickup-time"
                  />
                </div>
              </div>

              {/* Flight Number */}
              <div className="flex items-center gap-3">
                <Plane size={18} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Flugnummer (optional)"
                  value={flightNumber}
                  onChange={(e) => setFlightNumber(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  data-testid="flight-number"
                />
              </div>

              {/* Name on Sign */}
              <div className="flex items-center gap-3">
                <Users size={18} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Name auf dem Schild des Fahrers"
                  value={nameOnSign}
                  onChange={(e) => setNameOnSign(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  data-testid="name-on-sign"
                />
              </div>

              {/* Add Return */}
              <div className="flex items-center gap-3">
                <RotateCcw size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Rückfahrt hinzufügen</span>
                <button
                  onClick={() => setAddReturn(!addReturn)}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    addReturn ? 'bg-orange-500' : 'bg-slate-200'
                  }`}
                  data-testid="add-return-toggle"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    addReturn ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Return Date/Time */}
              {addReturn && (
                <div className="flex items-center gap-3 pl-8 animate-fadeIn">
                  <span className="text-sm text-slate-500 flex-1">Rückfahrt</span>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                      data-testid="return-date"
                    />
                    <input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                      data-testid="return-time"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Passengers */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
              {/* Adults */}
              <div className="flex items-center gap-3">
                <Users size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Erwachsene</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAdults(Math.max(1, adults - 1))}
                    className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center hover:bg-orange-50"
                    data-testid="adults-minus"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center font-medium">{adults}</span>
                  <button
                    onClick={() => setAdults(adults + 1)}
                    className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center hover:bg-orange-50"
                    data-testid="adults-plus"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="flex items-center gap-3">
                <Baby size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Kinder</span>
                <button
                  onClick={() => setShowChildrenModal(true)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                  data-testid="children-edit"
                >
                  {children > 0 ? `${children} Kinder, ${childSeats} Sitze` : 'Bearbeiten'}
                </button>
              </div>

              {/* Luggage */}
              <div className="flex items-center gap-3">
                <Briefcase size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Gepäck</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setLuggage(Math.max(0, luggage - 1))}
                    className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center hover:bg-orange-50"
                    data-testid="luggage-minus"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center font-medium" data-testid="luggage-count">{luggage}</span>
                  <button
                    onClick={() => setLuggage(luggage + 1)}
                    className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center hover:bg-orange-50"
                    data-testid="luggage-plus"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* Comment */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-3">
                <MessageSquare size={18} className="text-slate-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-slate-400 mb-1">Kommentar</p>
                  <textarea
                    placeholder="Gepäckinformation, spezielle Wünsche oder Aufgaben für den Fahrer"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                    data-testid="comment"
                  />
                </div>
              </div>
              
              {/* Quick Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {QUICK_TAGS.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'bg-orange-50 border-orange-500 text-orange-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`tag-${tag.id}`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Promo Code */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <button
                onClick={() => setShowPromo(!showPromo)}
                className="flex items-center gap-3 w-full text-left"
              >
                <Tag size={18} className="text-slate-400" />
                <span className="text-sm text-slate-700 flex-1">Ich habe einen Promo-Code</span>
                <div className={`w-12 h-6 rounded-full transition-colors relative ${
                  showPromo ? 'bg-orange-500' : 'bg-slate-200'
                }`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    showPromo ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </div>
              </button>
              {showPromo && (
                <input
                  type="text"
                  placeholder="Promo-Code eingeben"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  data-testid="promo-code"
                />
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="customer-terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                  data-testid="customer-terms-checkbox"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  Ich akzeptiere die{' '}
                  <a href="/agb" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline font-medium">
                    Allgemeinen Geschäftsbedingungen
                  </a>{' '}
                  und die{' '}
                  <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline font-medium">
                    Datenschutzerklärung
                  </a>{' '}
                  von 366Transfer.
                </span>
              </label>
            </div>

            {/* Submit Button */}
            {trustB && (
              <div className="mb-3 space-y-2">
                <TrustBadges variant="compact" />
                <CancellationBanner testIdSuffix="booking" />
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={isLoading || !acceptedTerms}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              data-testid="get-offers-btn"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Angebote erhalten <ArrowRight size={18} /></>
              )}
            </button>
          </div>

          {/* Right Side - Map */}
          <div className="lg:sticky lg:top-24 h-fit space-y-4">
            {/* Map */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden relative">
              <MapContainer
                center={mapCenter}
                zoom={10}
                className="w-full h-[400px] lg:h-[500px]"
                style={{ zIndex: 1 }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Start Marker */}
                <Marker position={[fromLocation.lat, fromLocation.lng]} icon={startIcon}>
                  <Popup>
                    <strong>Start:</strong><br />
                    {fromLocation.address?.split(',').slice(0, 2).join(', ')}
                  </Popup>
                </Marker>
                
                {/* End Marker */}
                <Marker position={[toLocation.lat, toLocation.lng]} icon={endIcon}>
                  <Popup>
                    <strong>Ziel:</strong><br />
                    {toLocation.address?.split(',').slice(0, 2).join(', ')}
                  </Popup>
                </Marker>
                
                {/* Route Line */}
                {routeCoordinates.length > 0 && (
                  <Polyline
                    positions={routeCoordinates}
                    pathOptions={{
                      color: '#22C55E',
                      weight: 5,
                      opacity: 0.9,
                    }}
                  />
                )}
                
                {/* Fit bounds to show both markers */}
                <FitBounds from={fromLocation} to={toLocation} />
              </MapContainer>
              
              {/* Route Info Overlay */}
              {routeInfo && (
                <div className="absolute bottom-4 left-4 bg-white rounded-xl shadow-lg px-4 py-3 border border-slate-100 z-[1000]">
                  <p className="text-2xl font-bold text-slate-800">{routeInfo.distance} km</p>
                  <p className="text-sm text-slate-500">~{routeInfo.durationText}</p>
                </div>
              )}
            </div>

            {/* Price Match Promise */}
            <div className="bg-emerald-50 rounded-2xl p-5 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <Check size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Preisgarantie</p>
                <p className="text-sm text-slate-600 mt-1">
                  Finden Sie ein ähnliches Angebot günstiger? Wir erstatten die Differenz!
                </p>
              </div>
            </div>

            {/* Selected Vehicle Info */}
            <div className="bg-slate-50 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ausgewähltes Fahrzeug</p>
                  <p className="font-semibold text-slate-800">
                    {VEHICLE_TYPES.find(v => v.id === selectedVehicle)?.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Geschätzter Preis</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ab €{calculatePrice(selectedVehicle)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Children Modal */}
      {showChildrenModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowChildrenModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-4">Kinder & Kindersitze</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Anzahl Kinder</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setChildren(Math.max(0, children - 1))} className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center">
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center font-medium">{children}</span>
                  <button onClick={() => setChildren(children + 1)} className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Kindersitze benötigt</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setChildSeats(Math.max(0, childSeats - 1))} className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center">
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center font-medium">{childSeats}</span>
                  <button onClick={() => setChildSeats(Math.min(children, childSeats + 1))} className="w-8 h-8 rounded-full border border-orange-500 text-orange-500 flex items-center justify-center">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowChildrenModal(false)}
              className="mt-6 w-full bg-orange-500 text-white py-3 rounded-xl font-semibold"
            >
              Fertig
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal - Login or Register */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget) resetAuthModal(); }} data-testid="booking-auth-modal-overlay">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl" onMouseDown={e => e.stopPropagation()} data-testid="booking-auth-modal">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="booking-auth-title">
                  {authStep === 'choose' && 'Anmelden oder Registrieren'}
                  {authStep === 'login' && 'Willkommen zurück'}
                  {authStep === 'register' && 'Konto erstellen'}
                  {authStep === 'verify' && 'E-Mail bestätigen'}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {authStep === 'choose' && 'Melden Sie sich an, um Ihre Transferanfrage zu senden'}
                  {authStep === 'login' && 'Mit Ihrem Konto anmelden'}
                  {authStep === 'register' && 'Erstellen Sie ein kostenloses Konto'}
                  {authStep === 'verify' && `Code wurde an ${authEmail} gesendet`}
                </p>
              </div>
              <button onClick={resetAuthModal} className="text-slate-300 hover:text-slate-500 p-1" data-testid="booking-auth-close">
                <X size={20} />
              </button>
            </div>

            {authStep === 'choose' && (
              <div className="space-y-3">
                <button onClick={() => setAuthStep('login')} className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-colors" data-testid="auth-choose-email">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <Mail size={20} className="text-slate-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Mit E-Mail anmelden</span>
                </button>

                <button onClick={handleGoogleLogin} className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-colors" data-testid="auth-choose-google">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
                  </div>
                  <span className="text-sm font-medium text-slate-700">Mit Google fortfahren</span>
                </button>

                <div className="pt-3 text-center">
                  <p className="text-xs text-slate-400">
                    Noch kein Konto?{' '}
                    <button onClick={() => setAuthStep('register')} className="text-orange-500 hover:underline font-medium" data-testid="auth-switch-to-register">
                      Jetzt registrieren
                    </button>
                  </p>
                </div>

                <p className="text-xs text-slate-400 text-center pt-2">
                  Mit der Registrierung akzeptieren Sie die{' '}
                  <a href="/agb" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline">AGB</a>{' '}
                  und die{' '}
                  <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline">Datenschutzerklärung</a>{' '}
                  von 366Transfer.
                </p>
              </div>
            )}

            {authStep === 'login' && (
              <form onSubmit={handleAuthLogin} className="space-y-3">
                <button type="button" onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 py-3 rounded-xl text-sm font-medium text-slate-700 transition-all hover:shadow-sm mb-2" data-testid="booking-google-login">
                  <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
                  Mit Google anmelden
                </button>
                <div className="flex items-center gap-3 my-3"><div className="flex-1 h-px bg-slate-200"></div><span className="text-xs text-slate-400">oder</span><div className="flex-1 h-px bg-slate-200"></div></div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Mail size={18} className="text-slate-400" />
                  <input type="email" placeholder="E-Mail-Adresse" value={authEmail} onChange={e => setAuthEmail(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" autoFocus data-testid="booking-auth-email" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Shield size={18} className="text-slate-400" />
                  <input type="password" placeholder="Passwort" value={authPassword} onChange={e => setAuthPassword(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="booking-auth-password" />
                </div>
                {authError && <p className="text-sm text-red-500 text-center" data-testid="booking-auth-error">{authError}</p>}
                <button type="submit" disabled={authLoading} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-auth-login-btn">
                  {authLoading ? <Loader2 size={16} className="animate-spin" /> : null} Anmelden
                </button>
                <p className="text-center text-sm text-slate-500">
                  Noch kein Konto? <button type="button" onClick={() => { setAuthStep('register'); setAuthError(''); }} className="text-orange-500 hover:underline font-medium" data-testid="booking-switch-register">Registrieren</button>
                </p>
              </form>
            )}

            {authStep === 'register' && (
              <form onSubmit={handleAuthRegister} className="space-y-3">
                <button type="button" onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 py-3 rounded-xl text-sm font-medium text-slate-700 transition-all hover:shadow-sm mb-2" data-testid="booking-google-register">
                  <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
                  Mit Google registrieren
                </button>
                <div className="flex items-center gap-3 my-3"><div className="flex-1 h-px bg-slate-200"></div><span className="text-xs text-slate-400">oder</span><div className="flex-1 h-px bg-slate-200"></div></div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <User size={18} className="text-slate-400" />
                  <input type="text" placeholder="Name" value={authName} onChange={e => setAuthName(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" autoFocus data-testid="booking-auth-name" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Mail size={18} className="text-slate-400" />
                  <input type="email" placeholder="E-Mail-Adresse" value={authEmail} onChange={e => setAuthEmail(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="booking-auth-reg-email" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Shield size={18} className="text-slate-400" />
                  <input type="password" placeholder="Passwort" value={authPassword} onChange={e => setAuthPassword(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="booking-auth-reg-password" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Phone size={18} className="text-slate-400" />
                  <input type="tel" placeholder="Mobilnummer" value={authPhone} onChange={e => setAuthPhone(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="booking-auth-reg-phone" />
                </div>
                {authError && <p className="text-sm text-red-500 text-center" data-testid="booking-auth-error">{authError}</p>}
                <button type="submit" disabled={authLoading} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-auth-register-btn">
                  {authLoading ? <Loader2 size={16} className="animate-spin" /> : null} Konto erstellen
                </button>
                <p className="text-center text-sm text-slate-500">
                  Bereits ein Konto? <button type="button" onClick={() => { setAuthStep('login'); setAuthError(''); }} className="text-orange-500 hover:underline font-medium" data-testid="booking-switch-login">Anmelden</button>
                </p>
              </form>
            )}

            {authStep === 'verify' && (
              <form onSubmit={handleAuthVerify} className="space-y-3">
                <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
                  <Shield size={18} className="text-slate-400" />
                  <input type="text" placeholder="6-stelliger Code" value={authCode} onChange={e => setAuthCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="flex-1 bg-transparent outline-none text-sm tracking-widest font-mono text-center text-lg" maxLength={6} autoFocus data-testid="booking-auth-verify-code" />
                </div>
                {authError && <p className="text-sm text-red-500 text-center" data-testid="booking-auth-error">{authError}</p>}
                <button type="submit" disabled={authLoading || authCode.length !== 6} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2" data-testid="booking-auth-verify-btn">
                  {authLoading ? <Loader2 size={16} className="animate-spin" /> : null} Code bestätigen
                </button>
                <button type="button" onClick={async () => { setAuthError(''); setAuthCode(''); try { await resendCode(authEmail); } catch {} }} className="block mx-auto text-sm text-orange-500 hover:underline mt-2" data-testid="booking-resend-code">
                  Code erneut senden
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      {trustB && <ExitIntentPopup whatsappNumber={process.env.REACT_APP_SUPPORT_WHATSAPP} />}
    </div>
  );
}
